import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { computed, inject } from '@angular/core';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  WidgetConfig,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { GlobalsStore } from '../globals.state';
import { DataLayerStore } from '../data-layer/data-layer.state';
import { GeoDataStore } from '../geo/geo-data.state';
import { EntityId } from '@ngrx/signals/entities';
import { withWidgetCoreFeatures } from './widget.core-features';
import { withGridMethods } from './widget.grid-methods';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { DataLayerTypeStore } from '../data-layer-type/data-layer-type.state';
import { IndicatorStore } from '../indicator/indicator.state';
import {
  millisecondsTimestamp,
  sanitizeFilename,
} from '../../utility/app.utility';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { ExportSheet } from '../../services/data/export-helper.service';
import { ComparisonValue, KeyValueDistricts } from '../../models/geo-data';

/**
 * Helper function to safely clone a widget by excluding non-cloneable properties
 */
function safeCloneWidget<T>(widget: T): T {
  if (!widget || typeof widget !== 'object') return widget;

  // Extract non-cloneable properties
  const { el, grid, component, ...cloneable } = widget as any;

  // Clone the safe properties
  const cloned = structuredClone(cloneable);

  // Restore references to non-cloneable properties
  if (el !== undefined) cloned.el = el;
  if (grid !== undefined) cloned.grid = grid;
  if (component !== undefined) cloned.component = component;

  return cloned as T;
}

export function withWidgetMethods() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withWidgetCoreFeatures(),
    withGridMethods(),
    withComputed(({ activeWidget, widgetType }) => ({
      currentWidgetConfig: computed(() => {
        return activeWidget();
      }),
      currentDataLayerType: computed(() => {
        const selectedWidget = activeWidget();
        if (selectedWidget) {
          return selectedWidget.dataLayerType as string;
        } else {
          return null;
        }
      }),
      currentDataLayerYear: computed(() => {
        const current = activeWidget();
        if (current) {
          return current.year as string;
        } else {
          return null;
        }
      }),
      currentDataLayer: computed(() => {
        const current = activeWidget();
        if (current) {
          return current.dataLayer as string;
        } else {
          return null;
        }
      }),
      currentIndicators: computed(() => {
        const current = activeWidget();
        if (current) {
          return current.indicators as string[];
        } else {
          return [];
        }
      }),
      isAxisBasedType: computed(() => {
        const type = widgetType();
        return type && ['scatter', 'bubble'].includes(type);
      }),
    })),
    withMethods(
      (
        {
          selected,
          gridLayers,
          currentDataLayer,
          activeWidget,
          update,
          triggerEvent,
          get,
          add,
        },
        gridWidgetConfigService = inject(GridWidgetConfigService),
        globalStore = inject(GlobalsStore),
        layerTypeStore = inject(DataLayerTypeStore),
        indicatorStore = inject(IndicatorStore),
        layerStore = inject(DataLayerStore),
        geoDataStore = inject(GeoDataStore),
        comparisonValueStore = inject(ComparisonValueStore)
      ) => {
        const widgetMethods = {
          initializeConfig(type: WidgetType): CustomGridStackWidget {
            const currentLayers = gridLayers();
            const defaultLayer = currentLayers[0]
              ? layerStore.getById(currentLayers[0])
              : globalStore.selectedLayer();
            const parent = defaultLayer?.parent;
            return gridWidgetConfigService.getGridItemConfig(
              {},
              {
                dataLayer: defaultLayer?._id,
                year: defaultLayer?.year + '',
                dataLayerType: parent,
                disabledFeatures: comparisonValueStore.keys(),
              },
              type
            );
          },
          widgetHook(
            layout: any,
            isDataWidget: boolean,
            configCreateStamp?: number
          ): CustomGridStackWidget {
            // Always clone to prevent mutation of original bookmark config
            const clonedLayout = safeCloneWidget(layout);

            if (!isDataWidget || clonedLayout.widget.context === 'external')
              return clonedLayout;

            const widgetConfig: WidgetConfig = clonedLayout.widget;
            const layer = layerStore.getById(widgetConfig.dataLayer);
            const latestByParent = layerStore.getLatestByParent(
              widgetConfig.dataLayerType
            );

            if (!layer && latestByParent) {
              widgetConfig.dataLayer = latestByParent._id || '';
              widgetConfig.year = String(latestByParent.year) || '';
            }
            const indicators =
              widgetConfig.indicators?.filter(
                id => !!indicatorStore.getById(id)
              ) || [];

            const indicator = indicatorStore.getById(widgetConfig.indicator)
              ? widgetConfig.indicator
              : indicators[0] || null;

            if (
              indicator &&
              indicators.length !== widgetConfig.indicators.length
            ) {
              widgetConfig.indicators = indicators;
            }

            // config disabled features
            if (configCreateStamp) {
              comparisonValueStore
                .asArray()
                .forEach((compValue: ComparisonValue) => {
                  if (
                    compValue.createdAt &&
                    configCreateStamp &&
                    millisecondsTimestamp(compValue.createdAt) >
                      millisecondsTimestamp(configCreateStamp) &&
                    !widgetConfig.disabledFeatures.includes(compValue.key)
                  ) {
                    widgetConfig.disabledFeatures.push(compValue.key);
                  }
                });
            }

            if (!indicator) widgetConfig.indicator = '';
            if (indicators.length <= 0) widgetConfig.indicators = [];

            return clonedLayout;
          },
          /**
           * @param id
           * @param dataLayerId
           */
          updateDistricts(id: EntityId, dataLayerId: string) {
            const current = get(id);
            const widget = current?.widget;
            const dataLayer = layerStore.getById(dataLayerId);
            if (
              !dataLayer ||
              !dataLayer.geoData ||
              !widget ||
              widget.context === 'external'
            )
              return;

            let districts: KeyValueDistricts | null = null;
            if (
              current.type !== 'map' &&
              widget.groupProperty === 'dataLayer' &&
              dataLayer.type === 'Point'
            ) {
              const geoDataIds = layerStore.getGeoDataIdsFromSiblings(
                dataLayer.parent
              );
              districts = geoDataStore.getDistrictsByIds(
                geoDataIds,
                widget.activeGroup || undefined
              );
            } else {
              const geoData = geoDataStore.getById(dataLayer.geoData);
              districts = geoData
                ? geoDataStore.getDistrictsById(
                    geoData._id,
                    widget.activeGroup || undefined
                  )
                : null;
            }

            const feature = districts
              ? widget.feature || Object.keys(districts)[0]
              : '';

            this.setWidgetProperty('features', districts, id);
            this.setWidgetProperty('feature', feature, id);
          },
          updateWidgetConfigById(
            id: EntityId,
            newConfig: WidgetConfig,
            bypassStateUpdate = false
          ): void {
            const widget = get(id);
            const baseWidget = widget ? widget.widget : activeWidget();
            const changes: Partial<CustomGridStackWidget> = {
              widget: { ...baseWidget, ...newConfig },
            };
            update(id, changes, !bypassStateUpdate);
          },
          isIndicatorInSelection(indicatorId: string) {
            const widget = activeWidget();
            return widget?.indicators?.includes(indicatorId) || false;
          },
          activeIndicatorSelection() {
            const widget = activeWidget();
            return widget?.indicators || [];
          },
          /**
           * updates one specific property for one widget
           * @param key
           * @param value
           * @param id
           * @param rerender
           * @param event
           */
          setWidgetProperty<T extends keyof WidgetConfig>(
            key: T,
            value: WidgetConfig[T],
            id?: EntityId,
            rerender = true,
            event = ''
          ) {
            const original = id ? get(id) : selected();
            const current = safeCloneWidget(original);
            const widget = current?.widget;

            if (widget && current && current.id && current.widget) {
              current.widget[key] = value;
              update(current.id, current, rerender);
              if (event) triggerEvent(event);
            }
          },
          /**
           * Updates one or multiple properties of the widget configuration.
           *
           * @param updates - An object where keys are WidgetConfig properties to update and values are their new values.
           * @param rerender - trigger rerender
           * @param id - get by widgetId
           */
          setWidgetProperties<T extends keyof WidgetConfig>(
            updates: Partial<WidgetConfig>,
            rerender?: boolean,
            id?: string,
            event?: string
          ) {
            const original = id ? get(id) : selected();
            const current = safeCloneWidget(original);
            const widget = current?.widget;

            if (widget && current && current.id && current.widget) {
              for (const key in updates) {
                if (Object.prototype.hasOwnProperty.call(updates, key)) {
                  const typedKey: T = key as T;
                  const value: Partial<WidgetConfig> = updates[typedKey];
                  widget[typedKey] = value as WidgetConfig[T]; // Safely assign the value
                  current.widget[typedKey] = value as WidgetConfig[T]; // Sync changes
                }
              }
              update(current.id, current, rerender, event); // Commit the update to the store
            }
          },
          getAsCopy(id: string, newId?: string) {
            const widget = get(id);
            if (widget) {
              const { grid, el, component, x, y, ...cleanWidget } = widget;
              const copy: CustomGridStackWidget = structuredClone(cleanWidget);
              copy.id = copy._id = newId || Date.now();

              if (newId) {
                copy.el = el;
                add(copy);
              }
              return copy;
            }
            return null;
          },
          widgetCaptionById(id: string, asFileName = false) {
            const widget = get(id)?.widget;
            if (!widget) return '';
            const layer = layerStore.getById(widget.dataLayer);
            const indicator = indicatorStore.getById(widget.indicator);
            const typeName =
              layerTypeStore.getById(widget.dataLayerType)?.name || '';
            let text = '';
            switch (widget.focusProperty) {
              case 'indicator':
                if (!indicator) return typeName;
                text = `${typeName}: ${indicatorStore.getFullName(indicator)}`;
                break;
              case 'dataLayer':
                text = `${typeName}: ${widget.year}`;
                break;
              case 'feature':
                if (!layer || !layer.geoData) return typeName;
                // eslint-disable-next-line no-case-declarations
                const feature =
                  geoDataStore.getDistrictByIdAndKey(
                    layer.geoData,
                    widget.feature
                  ) || comparisonValueStore.byKey()[widget.feature];
                text = `${typeName}: ${feature?.name}`;
                break;
              default:
                return '';
            }

            return asFileName ? sanitizeFilename(text) : text;
          },
          convertChartTo2DArray(
            chartData: any,
            focusProperty: DataGroupProperty
          ): ExportSheet {
            const label =
              focusProperty === 'feature'
                ? 'Teilraum'
                : focusProperty === 'dataLayer'
                  ? 'Jahr'
                  : 'Indikator';
            const headers: string[] = [
              label,
              ...chartData.datasets.map((ds: any) =>
                ds.group ? `${ds.group}: ${ds.label}` : ds.label
              ),
            ];
            const table: string[][] = [headers];

            chartData.labels.forEach((groupLabel: string, index: number) => {
              const row: string[] = [
                groupLabel,
                ...chartData.datasets.map(
                  (ds: any) => ds.data[index] || 'k. A.'
                ),
              ];
              table.push(row);
            });

            return { rows: table, title: 'Datenblatt' };
          },
        };
        return widgetMethods;
      }
    )
  );
}
